import { z } from 'zod';
import { KamCodecSchema, KamDtmfModeSchema, KamSrtpModeSchema, KamTransportSchema } from '../types/kam-settings';

/**
 * Contratto di scrittura trunk SIP condiviso da:
 * - route UI `POST/PUT /api/:slug/sip-trunks` (modale trunk);
 * - route integratori `POST/PUT /api/v2/voip/trunks` (API key);
 * - `SipTrunkModal.tsx` (tipo del payload).
 *
 * È volutamente NON strict: i client esistenti inviano campi extra che le
 * route ignorano o persistono in JSONB (`sip_config`, `security`, `gdpr`,
 * `provider_config`); il contratto fissa i campi che il backend interpreta.
 *
 * `did_config` arriva in forma libera e viene canonicalizzato (E.164
 * country-aware) e verificato contro i DID degli altri tenant dal gate
 * condiviso lato backend: `outbound_caller_id`≡`main_number` e
 * `inbound_dids`≡`did_pool` sono alias storici accettati in ingresso.
 */
export const SipTrunkDidConfigWriteSchema = z
  .object({
    main_number: z.string().max(64).nullish(),
    outbound_caller_id: z.string().max(64).nullish(),
    did_pool: z.array(z.string().max(64)).nullish(),
    inbound_dids: z.array(z.string().max(64)).nullish(),
  })
  .passthrough();

export type SipTrunkDidConfigWrite = z.infer<typeof SipTrunkDidConfigWriteSchema>;

const jsonObjectSchema = z.record(z.string(), z.unknown());

export const SIP_TRANSPORTS = ['udp', 'tcp', 'tls'] as const;
export const SipTransportSchema = z.enum(SIP_TRANSPORTS);
export type SipTransport = z.infer<typeof SipTransportSchema>;

/**
 * `sip_config` in scrittura: i campi che il backend interpreta sono tipizzati
 * (porta numerica, trasporto noto) e facoltativi — l'aggiornamento è parziale;
 * il resto del JSONB passa invariato (`passthrough`).
 */
export const SipTrunkSipConfigWriteSchema = z
  .object({
    host: z.string().max(255).optional(),
    port: z.number().int().min(1).max(65535).optional(),
    transport: SipTransportSchema.optional(),
    username: z.string().max(255).optional(),
    password: z.string().max(255).optional(),
    auth_realm: z.string().max(255).optional(),
    from_user: z.string().max(255).optional(),
    from_domain: z.string().max(255).optional(),
    register_proxy: z.string().max(255).optional(),
    outbound_proxy: z.string().max(255).optional(),
    contact_params: z.string().max(255).optional(),
    registration_ip: z.string().max(64).optional(),
    register: z.boolean().optional(),
  })
  .passthrough();
export type SipTrunkSipConfigWrite = z.infer<typeof SipTrunkSipConfigWriteSchema>;

export const SIP_TRUNK_STATUSES = ['active', 'inactive', 'testing'] as const;
export const SipTrunkStatusSchema = z.enum(SIP_TRUNK_STATUSES);
export const SIP_TRUNK_AUTH_MODES = ['register', 'ip_auth'] as const;
export const SipTrunkAuthModeSchema = z.enum(SIP_TRUNK_AUTH_MODES);

export const SipTrunkWriteBodySchema = z
  .object({
    /** Nome del trunk: `trunk_name` (UI) o `name` (v2); almeno uno in creazione. */
    trunk_name: z.string().trim().min(1).max(100).optional(),
    name: z.string().trim().min(1).max(100).optional(),
    provider: z.string().max(100).optional(),
    provider_type: z.string().max(50).optional(),
    status: SipTrunkStatusSchema.optional(),
    /** UUID sede lato UI; identificativo custom della sede lato v2 (risolto dalla route). */
    store_id: z.string().max(255).nullish(),
    auth_mode: SipTrunkAuthModeSchema.optional(),
    sip_config: SipTrunkSipConfigWriteSchema.optional(),
    did_config: SipTrunkDidConfigWriteSchema.nullish(),
    security: jsonObjectSchema.optional(),
    gdpr: jsonObjectSchema.optional(),
    provider_config: jsonObjectSchema.nullish(),
    codec_prefs: z.string().max(255).optional(),
    codec_preferences: z.array(z.string().max(32)).optional(),
    outbound_caller_id: z.string().max(64).optional(),
    inbound_dids: z.array(z.string().max(64)).optional(),
    max_concurrent_calls: z.number().int().min(1).max(10000).optional(),
    failover_trunk_id: z.string().uuid().nullish(),
    record_calls: z.boolean().optional(),
    record_path: z.string().max(512).nullish(),
    dtmf_mode: KamDtmfModeSchema.optional(),
    nat_traversal: z.boolean().optional(),
    nat_type: z.string().max(50).nullish(),
    session_timers: z.boolean().optional(),
    session_expires: z.number().int().min(0).optional(),
    retry_seconds: z.number().int().min(0).optional(),
    ping: z.number().int().min(0).optional(),
    caller_id_in_from: z.boolean().optional(),
    health_check_enabled: z.boolean().optional(),
    transport_pref: z.array(KamTransportSchema).optional(),
    codec_list: z.array(KamCodecSchema).optional(),
    srtp_mode: KamSrtpModeSchema.optional(),
    /** Solo v2: identificativo dell'integratore (la colonna `sip_trunks.external_id` è UUID). */
    external_id: z.string().uuid().optional(),
  })
  .passthrough();

export type SipTrunkWriteBody = z.infer<typeof SipTrunkWriteBodySchema>;

export const SipTrunkCreateRequestSchema = SipTrunkWriteBodySchema.refine(
  (body) => Boolean(body.trunk_name || body.name),
  { message: 'Trunk name is required', path: ['name'] },
);
export type SipTrunkCreateRequest = z.infer<typeof SipTrunkCreateRequestSchema>;

export const SipTrunkUpdateRequestSchema = SipTrunkWriteBodySchema;
export type SipTrunkUpdateRequest = z.infer<typeof SipTrunkUpdateRequestSchema>;

/**
 * Creazione via API v2 (integratori): oltre al nome sono obbligatori
 * `sip_config` e `did_config` (la UI tenant li deriva dal form, qui arrivano
 * dal chiamante). Ogni assenza è un issue Zod con il suo `path`, così il 400
 * `VALIDATION_ERROR` riporta il dettaglio per campo.
 */
export const SipTrunkV2CreateRequestSchema = SipTrunkWriteBodySchema.superRefine((body, ctx) => {
  if (!body.trunk_name && !body.name) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Trunk name is required', path: ['name'] });
  }
  if (!body.sip_config) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'sip_config is required', path: ['sip_config'] });
  }
  if (!body.did_config) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'did_config is required', path: ['did_config'] });
  }
});
export type SipTrunkV2CreateRequest = z.infer<typeof SipTrunkV2CreateRequestSchema>;

// ---------------------------------------------------------------------------
// Modello editor (form `SipTrunkModal.tsx`) — ex tipi locali del frontend.
// Descrive il trunk come lo vede la UI: campi del record `sip_trunks` letti
// dall'API + JSONB annidati tipizzati sui campi che l'editor compila.
// ---------------------------------------------------------------------------

export const SIP_ENCRYPTIONS = ['none', 'tls', 'srtp'] as const;
export const SipEncryptionSchema = z.enum(SIP_ENCRYPTIONS);
export type SipEncryption = z.infer<typeof SipEncryptionSchema>;

export type SipTrunkStatus = z.infer<typeof SipTrunkStatusSchema>;
export type SipTrunkAuthMode = z.infer<typeof SipTrunkAuthModeSchema>;
export type SipDtmfMode = z.infer<typeof KamDtmfModeSchema>;

/** `sip_trunks.sip_config` (JSONB) nei campi che la UI compila. */
export const SipTrunkSipConfigSchema = z
  .object({
    host: z.string().max(255),
    port: z.number().int().min(1).max(65535),
    transport: SipTransportSchema,
    username: z.string().max(255).optional(),
    password: z.string().max(255).optional(),
    auth_realm: z.string().max(255).optional(),
    from_user: z.string().max(255).optional(),
    from_domain: z.string().max(255).optional(),
    register_proxy: z.string().max(255).optional(),
    outbound_proxy: z.string().max(255).optional(),
    contact_params: z.string().max(255).optional(),
    registration_ip: z.string().max(64).optional(),
  })
  .passthrough();
export type SipTrunkSipConfig = z.infer<typeof SipTrunkSipConfigSchema>;

/** `sip_trunks.did_config` (JSONB) come lo compila l'editor. */
export const SipTrunkDidConfigSchema = z
  .object({
    main_number: z.string().max(64),
    did_pool: z.array(z.string().max(64)),
  })
  .passthrough();
export type SipTrunkDidConfig = z.infer<typeof SipTrunkDidConfigSchema>;

export const SipTrunkRateLimitSchema = z.object({
  enabled: z.boolean(),
  calls_per_minute: z.number().int().min(0),
  calls_per_hour: z.number().int().min(0),
});
export type SipTrunkRateLimit = z.infer<typeof SipTrunkRateLimitSchema>;

/** `sip_trunks.security` (JSONB). */
export const SipTrunkSecuritySchema = z
  .object({
    encryption: SipEncryptionSchema,
    authentication: z.enum(['none', 'digest', 'tls']),
    acl: z.array(z.string().max(64)),
    rate_limit: SipTrunkRateLimitSchema,
  })
  .passthrough();
export type SipTrunkSecurity = z.infer<typeof SipTrunkSecuritySchema>;

export const SipTrunkEditorModelSchema = z.object({
  id: z.string().optional(),
  tenant_id: z.string().optional(),
  store_id: z.string().optional(),
  trunk_name: z.string(),
  name: z.string().optional(),
  provider: z.string(),
  provider_type: z.string().optional(),
  status: SipTrunkStatusSchema,
  auth_mode: SipTrunkAuthModeSchema.optional(),
  has_credentials: z.boolean().optional(),
  credentials_masked: z.string().optional(),
  sip_config: SipTrunkSipConfigSchema.optional(),
  did_config: SipTrunkDidConfigSchema.optional(),
  security: SipTrunkSecuritySchema.optional(),
  outbound_caller_id: z.string().optional(),
  max_concurrent_calls: z.number().int().optional(),
  codec_prefs: z.string().optional(),
  codec_preferences: z.array(z.string()).optional(),
  dtmf_mode: KamDtmfModeSchema.optional(),
  nat_traversal: z.boolean().optional(),
  session_timers: z.boolean().optional(),
  session_expires: z.number().int().optional(),
  retry_seconds: z.number().int().optional(),
  ping: z.number().int().optional(),
  caller_id_in_from: z.boolean().optional(),
  failover_trunk_id: z.string().nullable().optional(),
  health_check_enabled: z.boolean().optional(),
  record_calls: z.boolean().optional(),
  transport_pref: z.array(KamTransportSchema).optional(),
  codec_list: z.array(KamCodecSchema).optional(),
  srtp_mode: KamSrtpModeSchema.optional(),
  enabled: z.boolean().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});
export type SipTrunkEditorModel = z.infer<typeof SipTrunkEditorModelSchema>;

// ---------------------------------------------------------------------------
// POST /api/v2/voip/trunks/sync — sincronizzazione bidirezionale integratori.
// ---------------------------------------------------------------------------

export const SipTrunkSyncItemSchema = z
  .object({
    /** Identificativo dell'integratore: la colonna `sip_trunks.external_id` è UUID. */
    external_id: z.string().uuid().optional(),
    name: z.string().max(100).optional(),
    provider: z.string().max(100).optional(),
    status: SipTrunkStatusSchema.optional(),
    sip_config: SipTrunkSipConfigWriteSchema.optional(),
    did_config: jsonObjectSchema.optional(),
    security: jsonObjectSchema.optional(),
    gdpr: jsonObjectSchema.optional(),
    codec_preferences: z.array(z.string().max(32)).optional(),
    /** Timestamp remoto: vince sul record locale solo se più recente (o con `force`). */
    updated_at: z.string().optional(),
  })
  .passthrough();
export type SipTrunkSyncItem = z.infer<typeof SipTrunkSyncItemSchema>;

/**
 * Busta del sync: solo la forma esterna è vincolante (400 se non è un array).
 * Gli elementi restano `unknown` e vengono validati UNO PER UNO nel ciclo
 * (`SipTrunkSyncItemSchema`), così un elemento malformato finisce in `errors`
 * senza abortire il batch degli altri.
 */
export const SipTrunkSyncRequestSchema = z
  .object({
    force: z.boolean().default(false),
    trunks: z.array(z.unknown()).default([]),
  })
  .passthrough();
export type SipTrunkSyncRequest = z.infer<typeof SipTrunkSyncRequestSchema>;
